import {
  createFileRoute,
  Outlet,
  useNavigate,
  useLocation,
  useRouterState,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { SessionRecoveryAlert } from '@/components/auth/session-recovery-alert';
import { BottomNavigation } from '@/components/layout';
import { LaunchShell } from '@/components/v2/logo';
import { useAuth, useAuthInitialized } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { settingsV2Api } from '@/lib/api/v2/settings';
import { whenIdle } from '@/lib/prefetch/idle';
import { normalizeHexColor } from '@/lib/utils/color';
import { useIsAuthenticated } from '@/hooks';
import pageEaseInStyles from '@/routes/page-ease-in.module.less';

// Onboarding (tutorial + embla, ~221KB) renders once per learner, so its
// module is imported only when the gate below will actually render it. The
// gate decision itself is synchronous; learners waiting on the import see the
// same LaunchShell they saw pre-init, so there is no flash.
const LazyLearnerOnboarding = lazy(() =>
  import('@/components/v2/onboarding/learner-onboarding').then(({ LearnerOnboarding }) => ({
    default: LearnerOnboarding,
  }))
);

/**
 * Authenticated Layout Route
 */
export const Route = createFileRoute('/_authenticated')({
  // SPA shell (rebuild proposal step 1): the authenticated subtree renders
  // client-side only. The server skips its render, beforeLoad and data
  // entirely (router-core load-server honours match.ssr === false), returning
  // the root shell immediately — document loads of authed pages boot the app
  // instead of waiting for an SSR stream, and no authed page ever hydrates.
  // Auth is enforced client-side below (AuthContext) and by the API on every
  // request; deep-link protection for unauthenticated document loads stays on
  // the root session context. Public entity pages keep SSR for share previews.
  ssr: false,
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return <AuthenticatedContent />;
}

export function AuthenticatedContent() {
  const { isAuthenticated, isError, user } = useIsAuthenticated(); // Destructure auth state
  const queryClient = useQueryClient();
  const { setBaseTheme, setAccentColor, setCustomAccentColor } = useTheme();
  const themeSetters = useRef({ setBaseTheme, setAccentColor, setCustomAccentColor });
  themeSetters.current = { setBaseTheme, setAccentColor, setCustomAccentColor };
  const { authState } = useAuth();
  const isInitialized = useAuthInitialized();
  const navigate = useNavigate();
  const location = useLocation();
  // Page ease-in key: pathname only, so search/hash changes do not restart the fade.
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    // Server settings win after authentication, including refreshes during setup.
    // Route through the canonical ['settings'] query key (issue #913) so the
    // AppTab consumer shares one in-flight request and one cache entry instead
    // of issuing a second GET /v2/me/settings per settings visit.
    if (!isAuthenticated) return;
    let active = true;
    // Settings is a background warm (theme + accent for the App tab) and is
    // never something this navigation waits on: schedule it while the main
    // thread is idle so it cannot compete with the page being rendered.
    const cancel = whenIdle(() => {
      void queryClient
        .ensureQueryData({
          queryKey: ['settings'],
          queryFn: async () => {
            const result = await settingsV2Api.getSettings();
            if (!result.ok) throw new Error(result.error.message);
            return result.value;
          },
        })
        .then((settings) => {
          if (!active) return;
          const accent = normalizeHexColor(settings.accent_color);
          if (settings.theme === 'light' || settings.theme === 'dark')
            themeSetters.current.setBaseTheme(settings.theme);
          if (!accent) return;
          const preset = {
            '#3b82f6': 'blue',
            '#8b5cf6': 'purple',
            '#10b981': 'emerald',
            '#f97316': 'orange',
          }[accent];
          if (preset)
            themeSetters.current.setAccentColor(preset as 'blue' | 'purple' | 'emerald' | 'orange');
          else {
            themeSetters.current.setCustomAccentColor(accent);
            themeSetters.current.setAccentColor('custom');
          }
        })
        // A settings fetch failure must not become an unhandled rejection: the
        // theme keeps its defaults and AppTab shows its retryable error state.
        .catch(() => undefined);
    });
    return () => {
      active = false;
      cancel();
    };
  }, [isAuthenticated, user?.id, queryClient]);

  // Check if bottom navigation should be hidden
  // Hide on: detail pages, form pages, and special content pages
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const shouldHideBottomNav =
    (pathSegments[0] === 'community' && pathSegments.length >= 2) ||
    // Feature requests pages
    location.pathname.startsWith('/feature-requests') ||
    // Data report form (guided full-screen form with its own bottom action)
    location.pathname.startsWith('/data-reports') ||
    // Add-listing form (full-screen form with its own bottom action)
    location.pathname.startsWith('/listings') ||
    location.pathname.startsWith('/feature-request/') ||
    location.pathname.startsWith('/class/') ||
    location.pathname.startsWith('/studios/') ||
    location.pathname.startsWith('/instructors/') ||
    location.pathname.startsWith('/discovery/studios/map') ||
    // Market product detail + purchase flows (transactional/detail pages)
    location.pathname.startsWith('/market/') ||
    location.pathname.startsWith('/purchase/') ||
    location.pathname.startsWith('/profile/purchases/') ||
    // Review form pages (new and edit)
    location.pathname.includes('/reviews/new') ||
    location.pathname.match(/\/reviews\/[^/]+\/edit/) ||
    // Profile edit pages
    location.pathname.includes('/profile/edit') ||
    location.pathname.includes('/profile/settings');

  // Client-side auth check with redirect
  useEffect(() => {
    if (isInitialized && authState !== 'unavailable' && !isError && !isAuthenticated) {
      // ✅ Prevent infinite redirect loop: Don't redirect if already on login page
      if (location.pathname === '/login') {
        return;
      }

      // Redirect to login if not authenticated
      navigate({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
  }, [
    authState,
    isInitialized,
    isAuthenticated,
    isError,
    navigate,
    location.href,
    location.pathname,
  ]);

  if (!isInitialized) return <LaunchShell />;
  if (isError || authState === 'unavailable') return <SessionRecoveryAlert centered />;
  if (!isAuthenticated) return <LaunchShell />;

  // Server session data is authoritative, so the gate also covers deep links.
  if (user?.role === 'dancer' && user.learner_onboarding_completed_at == null) {
    return (
      <Suspense fallback={<LaunchShell />}>
        <LazyLearnerOnboarding
          displayName={user.display_name}
          onComplete={async () => {
            const { settingsV2Api } = await import('@/lib/api/v2/settings');
            const result = await settingsV2Api.completeOnboarding();
            if (!result.ok)
              throw new Error(result.error.message || 'Could not complete onboarding');
            queryClient.setQueryData(['auth', 'session'], {
              ...user,
              learner_onboarding_completed_at: result.value.learner_onboarding_completed_at,
            });
          }}
        />
      </Suspense>
    );
  }

  // Render authenticated content
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      {/* App-wide page ease-in: keyed by pathname so every navigation (including
          back) remounts this container and replays the fade, while search-only
          changes (tab/filter switches) keep the key and do not re-fade. Same
          flex styles as before — the routed content's layout context is
          unchanged, only the fade + remount are added. BottomNavigation stays
          outside, so the shell chrome never re-fades.
          Page scroll is handled by .rootDocumentAppContainer (see
          __root.module.less). Keep this wrapper overflow-free: a nested
          non-scrolling overflow container here is the nearest scroll ancestor
          for children, which silently breaks position:sticky (e.g. discovery
          tabs/calendar/filter bar, time labels). */}
      <div
        key={pathname}
        className={pageEaseInStyles.pageEaseIn}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Outlet />
      </div>

      {!shouldHideBottomNav && <BottomNavigation />}
    </div>
  );
}
